"use client";

import { useState } from "react";
import { useRouter } from "@bprogress/next";
import {
  ArrowRightIcon,
  CheckIcon,
  LoaderIcon,
  SettingsIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { SuperLink } from "@/components/common/super-link";
import { PictureAsset } from "@/components/common/picture-asset";
import { getAssetImage } from "@/service/image/asset";
import { clientPost } from "@/service/client-api/client-api";

// ~7.5 s/pytanie → 103 pytań ≈ 13 min (zgodnie z designem).
const SECONDS_PER_QUESTION = 7.5;

// Ile pytań dodać po kliknięciu „Rozwiąż więcej pytań" (jak w NoMoreQuestionsView).
const ADD_MORE_COUNT = 20;

const pluralPytania = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return "pytanie";
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14))
    return "pytania";
  return "pytań";
};

const Stat = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-1 flex-col items-center gap-1.5">
    <p className="text-card-foreground text-base leading-none font-bold">
      {value}
    </p>
    <p className="text-muted-foreground text-sm leading-none">{label}</p>
  </div>
);

// Wariant statystyki dla stanu ukończonego — zamiast liczby fioletowy „ptaszek".
const DoneStat = ({ label }: { label: string }) => (
  <div className="flex flex-1 flex-col items-center gap-1.5">
    <span className="flex size-5 items-center justify-center rounded-full bg-violet-500">
      <CheckIcon className="size-3 text-white" strokeWidth={3} />
    </span>
    <p className="text-muted-foreground text-sm leading-none">{label}</p>
  </div>
);

// Stan „brak konfiguracji" — kropkowana karta z ikoną, zgodnie z designem.
// Całość klikalna → strona konfiguracji.
const NotConfiguredCard = ({ configureHref }: { configureHref: string }) => (
  <SuperLink
    href={configureHref}
    className="group bg-card block overflow-hidden rounded-lg border border-zinc-100 p-3 shadow-[4px_8px_19px_0px_rgba(139,92,246,0.15)] transition duration-300 ease-out hover:-translate-y-1 hover:shadow-[4px_8px_19px_0px_rgba(139,92,246,0.3)]"
  >
    {/* Warianty `dark:` jak na kafelku pustych podejść: przemapowanie palety nie sięga
        `group-hover:` ani kolorów podanych hexem, więc bez nich w ciemnym motywie świeciło
        jasnofioletowe obramowanie, a najechanie kładło na kafelek jasną mgiełkę. */}
    <div className="flex min-h-[145px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#c9a8ff] px-3 py-6 text-center transition-colors group-hover:border-violet-400 group-hover:bg-violet-50/40 dark:border-[#5b21b6] dark:group-hover:border-violet-500 dark:group-hover:bg-violet-400/10">
      <span className="flex size-11 items-center justify-center rounded-full bg-violet-100 transition-transform duration-500 ease-out group-hover:scale-110 group-hover:rotate-180">
        <SettingsIcon className="size-5 text-violet-500" />
      </span>
      <p className="text-card-foreground text-base leading-none font-semibold">
        Konfiguruj Spaced repetition
      </p>
      <p className="text-muted-foreground text-sm leading-5">
        Rozpocznij inteligentną naukę na podstawie historii odpowiedzi.
        <br />
        Zapamiętuj na dłużej, ucząc się krócej.
      </p>
    </div>
  </SuperLink>
);

const CardImage = () => (
  <div className="h-40 w-full shrink-0 overflow-hidden rounded-[5px] bg-violet-50 lg:h-full lg:w-[690px] lg:flex-1">
    <PictureAsset
      source={getAssetImage("/exams/illustrations/spaced-repetition")}
      ext="png"
      alt={"spaced repetition"}
      className="h-full w-full object-cover"
      wrapperClassName="block h-full w-full"
    />
  </div>
);

export const SpacedRepetitionCard = ({
  newCount,
  overdueCount,
  reviewCount,
  completedCount = 0,
  notConfigured = false,
  startHref,
  configureHref,
}: {
  newCount: number;
  overdueCount: number;
  reviewCount: number;
  completedCount?: number;
  notConfigured?: boolean;
  startHref: string;
  configureHref: string;
}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const total = newCount + overdueCount + reviewCount;
  const minutes = Math.max(1, Math.round((total * SECONDS_PER_QUESTION) / 60));
  const isDone = total === 0;

  async function onAddMoreClick() {
    setLoading(true);
    try {
      await clientPost(
        "exam.spaced_repetition.add_more_questions_today",
        {},
        { count: ADD_MORE_COUNT },
      );
      router.push(startHref);
    } catch (error) {
      console.error("Failed to add questions today:", error);
      setLoading(false);
    }
  }

  return (
    <div className="my-6 flex flex-col gap-2.5">
      {/* Nagłówek */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-xs leading-4 font-bold text-violet-500">
            Spaced repetition
          </p>
          <p className="text-lg leading-8 font-semibold tracking-[-0.4px] text-zinc-900">
            {notConfigured
              ? "Rozpocznij inteligentną naukę"
              : "Dzisiejsza dawka"}
          </p>
        </div>
        <Button
          variant="outline"
          size={"sm"}
          aria-label="Ustawienia spaced repetition"
          asChild
        >
          <SuperLink href={configureHref}>
            <SettingsIcon />
            Konfiguruj
          </SuperLink>
        </Button>
      </div>

      {/* Karta */}
      {notConfigured ? (
        <NotConfiguredCard configureHref={configureHref} />
      ) : (
        <div className="bg-card flex flex-col items-center gap-3 overflow-hidden rounded-lg border border-zinc-100 p-3 shadow-[4px_8px_19px_0px_rgba(139,92,246,0.15)] lg:flex-row">
          <CardImage />

          {/* Statystyki + akcja */}
          <div className="flex w-full flex-1 flex-col items-center gap-4 px-3">
            <div className="flex w-full items-start justify-between">
              {isDone ? (
                <>
                  <DoneStat label="Nowe" />
                  <DoneStat label="Zaległe" />
                  <DoneStat label="Powtórki" />
                </>
              ) : (
                <>
                  <Stat value={newCount} label="Nowych" />
                  <Stat value={overdueCount} label="Zaległych" />
                  <Stat value={reviewCount} label="Powtórki" />
                </>
              )}
            </div>

            <div className="flex w-full flex-col items-center gap-4">
              {isDone ? (
                <>
                  <Button
                    variant="outline"
                    onClick={onAddMoreClick}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? (
                      <LoaderIcon className="animate-spin" />
                    ) : (
                      <>
                        Rozwiąż więcej pytań
                        <ArrowRightIcon />
                      </>
                    )}
                  </Button>
                  {completedCount > 0 && (
                    <p className="text-muted-foreground text-sm">
                      Świetna robota. Przerobione pytania dzisiaj:{" "}
                      {completedCount}.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <Button variant="violet" asChild className="w-full">
                    <SuperLink href={startHref}>
                      Ucz się ({total} {pluralPytania(total)})
                      <ArrowRightIcon />
                    </SuperLink>
                  </Button>
                  <p className="text-muted-foreground text-sm">
                    Dzisiejsze pytania to ok. ~{minutes} min nauki
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
